import * as React from 'react';
import ReactTable from 'react-table'
import cx from 'classnames';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n';

import { SORT_DIRECTION } from '@common/constants';
import { Actions } from './components/Actions';
import { Filters } from './components/Filters';
import { FieldSelector } from '@common/components/FieldSelector';
import { columnHasSomeActiveFilter, denormalizeFilters, getScrollableElement, normalizeFilters } from './Table.utils';
import { TABLE_COLUMN_PREDEFINED_ACTION_TYPE } from './Table.constants';
import 'react-table/react-table.css';

import './Table.styles.scss';

const getDefaultResizerProps = () => {
	return {
		className: 'fa fa-ellipsis-v'
	};
};

const headerCellClassName = 'Table__header__cell';
const headerClassName='rt-tr';

const DEFAULT_COLUMN_INDEX_VALUE = undefined;

export const HIGHLIGHT_BACKGROUND_COLOR = '#cfe2fa';

export class Table extends React.Component {
	static propTypes = {
		columnActions: PropTypes.arrayOf(PropTypes.shape({
			className: PropTypes.string,
			label: PropTypes.string,
			type: PropTypes.string,
			onClick: PropTypes.func,
		})),
		headerActions: PropTypes.arrayOf(PropTypes.shape({
			className: PropTypes.string,
			label: PropTypes.string,
			type: PropTypes.string,
			onClick: PropTypes.func,
		})),
		columnsManagement: PropTypes.arrayOf(PropTypes.shape({
			refreshDataOnChange: PropTypes.bool.isRequired
		})),
		rowContextMenuActions: PropTypes.arrayOf(PropTypes.shape({
			label: PropTypes.string.isRequired,
			onClick: PropTypes.func.isRequired,
		})),
		onRowContextMenu: PropTypes.func,
	};

	static defaultProps = {
		infiniteScrollThreshold: 100,
		loadItemsCount: 45,
		idProperty: 'globalId',
		serverSorting: true,
		setLoadMetadata: () => {},
		columnActions: [],
		headerActions: [],
	};

	ref = React.createRef();
	createScrollHandler;
	headerActionsCoordinates = {
		x: 0,
		y: 0,
	};
	denormalizedFiltersValue = {};

	state = {
		currentContextMenuColumnIndex: DEFAULT_COLUMN_INDEX_VALUE,
		loading: false,
		isOpenedFieldSelector: false,
		areOpenedHeaderActions: false,
		currentHeaderActions: [],
		areOpenedFilters: false,
		selectedItems: [],
		loadMetadata: {
			pagination: {
				start: 0,
				size: this.props.loadItemsCount,
			}, 
			sorting: {},
			filtering: {}
		},
		rowContextMenuPosition: undefined,
	};

	constructor(props) {
		super(props);
		
		this.createScrollHandler = debounce(this.handleScroll, 50);
	}

	componentDidUpdate(prevProps, prevState) {
		if (!isEqual(prevProps.loadMetadata?.filtering, this.props.loadMetadata?.filtering)) {
			this.denormalizedFiltersValue = denormalizeFilters(this.props.loadMetadata?.filtering);
		}

		const shouldInitializeColumnActions = 
			(
				prevProps.config.columns?.length !== this.props.config.columns?.length ||
				prevProps.columnActions?.length !== this.props.columnActions?.length
			) &&
			this.withColumnActions;

		if (shouldInitializeColumnActions) {
			this.disposeColumnsActions();
			this.initializeColumnsActions();
		}

		const shouldInitializeHeaderActions = 
			prevProps.headerActions?.length !== this.props.headerActions?.length &&
			this.withHeaderActions;

		if (shouldInitializeHeaderActions) {
			this.disposeHeaderActions();
			this.initializeHeaderActions();
		}


		const getIdsFromSelectedItems = (items) => items?.map((x) => x[this.props.idProperty]);

		const selectedItemsUpdatedOutside = 
		 	isEqual(
				getIdsFromSelectedItems(prevState.selectedItems),
				getIdsFromSelectedItems(this.state.selectedItems)
			) && 
			this.props.selectedItems && 
			!isEqual(
				getIdsFromSelectedItems(this.state.selectedItems), 
				getIdsFromSelectedItems(this.props.selectedItems)
			);

		if (selectedItemsUpdatedOutside) {
			this.setState((x) => ({
				...x,
				selectedItems: this.props.selectedItems,
			}));
		}
	}

	componentDidMount() {
		const { infiniteScroll, disableAutoInit } = this.props;

		if (!disableAutoInit) {
			this.loadData();
		}

		if (this.scrollElement && infiniteScroll) {
			this.initializeLazyLoading();
		}

		if (this.withColumnActions && this.header) {
			this.initializeColumnsActions();
		}

		if (this.withHeaderActions && this.header) {
			this.initializeHeaderActions();
		}
	}

	componentWillUnmount() {
		const { infiniteScroll } = this.props;

		if (this.scrollElement && infiniteScroll) {
			this.disposeLazyLoading();
		}

		if (this.header) {
			this.disposeColumnsActions();
			this.disposeHeaderActions();
		}
	}

	initializeLazyLoading = () => {
		this.scrollElement.addEventListener('scroll', this.createScrollHandler);
	}

	disposeLazyLoading = () => {
		this.scrollElement.removeEventListener('scroll', this.createScrollHandler);
	}

	get headerCells() {
		return this.header
			? this.header.querySelectorAll(`.${headerCellClassName}`)
			: [];
	}

	initializeHeaderActions = () => {
		this.header.addEventListener('contextmenu', this.handleHeaderContextMenuOpen);
	}

	disposeHeaderActions = () => {
		this.header.removeEventListener('contextmenu', this.handleHeaderContextMenuOpen);
	}

	handleHeaderContextMenuOpen = (e) => {
		this.headerActionsCoordinates.x = e.clientX;
		this.headerActionsCoordinates.y = e.clientY;
		
		this.setCurrentContextMenuColumnIndex(DEFAULT_COLUMN_INDEX_VALUE);
		this.openHeaderActions(this.processedHeaderActions);
	}

	initializeColumnsActions = () => {
		this.headerCells.forEach((x) =>  { 
			x.addEventListener('contextmenu', this.handleColumnContextMenuOpen);
		});
	}

	disposeColumnsActions = () => {
		this.headerCells.forEach((x) =>  { 
			x.removeEventListener('contextmenu', this.handleColumnContextMenuOpen);
		});
	}

	handleColumnContextMenuOpen = (e) => {
		e.stopPropagation();
		e.preventDefault();

		const { currentTarget } = e;
		let headerCell = null;
		
		if (currentTarget.classList.contains(headerCellClassName)) {
			headerCell = currentTarget;
		}

		if (headerCell) {
			const { dataset: { index } } = headerCell;

			this.setCurrentContextMenuColumnIndex(Number(index));
		} else {
			this.setCurrentContextMenuColumnIndex(DEFAULT_COLUMN_INDEX_VALUE);
		}

		this.headerActionsCoordinates.x = e.clientX;
		this.headerActionsCoordinates.y = e.clientY;

		this.openHeaderActions(this.processedColumnActions);
	}

	setCurrentContextMenuColumnIndex = (currentContextMenuColumnIndex) => {
		this.setState((state) => ({ ...state, currentContextMenuColumnIndex }));
	}

	openHeaderActions = (currentHeaderActions) => {
		this.setState((state) => ({ 
			...state, 
			areOpenedHeaderActions: true,
			currentHeaderActions,
		}));
	}

	closeHeaderActions = () => {
		this.setState((state) => ({ ...state, areOpenedHeaderActions: false }));
	}

	setLoading = (loading) => {
		this.setState((state) => ({ ...state, loading }));
	}

	get shouldIgnoreLazyLoading() {
		const { config: { data }, itemsCount } = this.props;
		const shouldIgnore = this.state.loading || data.length === itemsCount || data.length > itemsCount;

		return shouldIgnore;
	}

	get loadMetadata() {
		const mergedLoadMetadata = {
			...this.state.loadMetadata,
			...this.props.loadMetadata,
		};

		return mergedLoadMetadata;
	}

	updateLoadMetadata = (loadMetadata) => {
		this.props.setLoadMetadata(loadMetadata);
		this.setState((state) => ({ ...state, loadMetadata }));
	}

	loadData = async (metadataToUpdate, options = { scrollToTop: true }) => {
		this.setLoading(true);

		try {
			const { onLoadData } = this.props;
			const metadata = {
				...this.loadMetadata,
				...metadataToUpdate,
			};

			await onLoadData(metadata);
			
			if (options.scrollToTop) {
				this.scrollToTop();
			}

			this.updateLoadMetadata(metadata);
		} finally {
			this.setLoading(false);
		}
	}

	lazilyLoadData = () => {
		const { loadItemsCount, config: { data } } = this.props;

		this.loadData(
			{
				pagination: {
					...this.loadMetadata.pagination,
					start: data.length,
					size: loadItemsCount,
				}
			},
			{
				scrollToTop: false
			}
		);
	}

	applyFilters = (filtersValue) => {
		this.denormalizedFiltersValue = {
			...this.denormalizedFiltersValue,
			[this.state.currentContextMenuColumnIndex]: filtersValue
		};

		this.closeFilters();
		this.loadData({
			pagination: {
				...this.loadMetadata.pagination,
				start: 0,
				size: this.props.loadItemsCount,
			},
			filtering: {
				filter: true,
				...normalizeFilters(this.denormalizedFiltersValue),
			},
		});
		this.setCurrentContextMenuColumnIndex(DEFAULT_COLUMN_INDEX_VALUE);
	}

	clearFilters = () => {
		this.setState(
			(state) => {
				delete this.denormalizedFiltersValue[state.currentContextMenuColumnIndex];

				const hasFilters = Object.values(this.denormalizedFiltersValue).some(columnHasSomeActiveFilter);
				const normalizedFilters = normalizeFilters(this.denormalizedFiltersValue);

				return {
					...state,
					loadMetadata: {
						...state.loadMetadata,
						pagination: {
							...state.loadMetadata?.pagination,
							start: 0,
						},
						filtering: {
							...normalizedFilters,
							filter: hasFilters,
						},
					},
				};
			}, 
			() => {
				this.setCurrentContextMenuColumnIndex(DEFAULT_COLUMN_INDEX_VALUE);
				this.loadData(this.state.loadMetadata);
			}
		);
	}

	crossedThreshold = (scrollHeight, offsetHeight, scrollTop) => {
		if (scrollHeight < offsetHeight) {
			return false;
		}

		const { infiniteScrollThreshold } = this.props;
		const currentScrollPosition = Math.round(offsetHeight + scrollTop);
		const distanceToBottom = Math.round(scrollHeight - currentScrollPosition);

		return distanceToBottom < infiniteScrollThreshold;
	}

	handleScroll = ({ target }) => {
		if (this.shouldIgnoreLazyLoading) {
			return;
		}

		const { scrollHeight, offsetHeight, scrollTop } = target;
		const crossedThreshold = this.crossedThreshold(scrollHeight, offsetHeight, scrollTop);

		if (crossedThreshold) {
			this.lazilyLoadData();
		}
	}

	getRootProps = () => {
		return {
			ref: this.ref
		};
	}

	get scrollElement() {
		if (!this.ref.current) {
			return null;
		}

		return getScrollableElement(this.ref.current);
	}

	get header() {
		if (!this.ref.current) {
			return null;
		}

		return this.ref.current.querySelector(`.${headerClassName}`);
	}

	handleSort = (e) => {
		e.persist();

		const { dataset: { index: columnIndex, sortDirection: columnSortDirection } } = e.currentTarget;

		const oppositeDirection = {
			[SORT_DIRECTION.asc]: SORT_DIRECTION.desc,
			[SORT_DIRECTION.desc]: SORT_DIRECTION.asc,
		};
		let direction = SORT_DIRECTION.desc;

		if (columnSortDirection) {
			direction = oppositeDirection[columnSortDirection];
		}
		
		this.loadData({
			pagination: {
				...this.loadMetadata.pagination,
				start: 0,
				size: this.props.loadItemsCount,
			},
			sorting: {
				...this.loadMetadata.sorting,
				columnIndex: Number(columnIndex),
				direction,
				sort: true,
			}
		});
	}

	renderHeaderCell = (config) => {
		const { loadMetadata: { sorting, filtering } } = this;
		const columnToBeFiltered = filtering?.columnToBeFiltered || [];
		const isFiltered = columnToBeFiltered.some((index) => index === config.index);
		const isSorted = sorting?.columnIndex == config.index;
		const sortingDirection = sorting?.direction;
		const hasSortIcon = isSorted && sortingDirection;
		
		return (
			<div 
				className={cx('Table__header__cell', { 'Table__header__cell-default': !config.sortable, 'Table__header__cell-disabled': config.disableActions }, headerCellClassName)} 
				data-index={config.index}
				data-sort-direction={sortingDirection}
				onClick={config.sortable ? this.handleSort : undefined}
			>
				{hasSortIcon && (
					<span className={`fa ${sortingDirection === SORT_DIRECTION.asc ? 'fa-caret-up' : 'fa-caret-down'} Table__header__cell__sort-icon`} />
				)}
				<div
					style={{
						width: `calc(100% - ${hasSortIcon ? '8px' : '0px'} - ${isFiltered ? '8px' : '0px'})`
					}}
					className="Table__header__cell__text"
				>
					{config.content}
				</div>
				{isFiltered && <span className="fa fa-filter Table__header__cell__filter-icon" />}
			</div>
		);
	}

	openFilters = () => {
		this.setState((state) => ({ ...state, areOpenedFilters: true }));
	}

	closeFilters = () => {
		this.setState((state) => ({ ...state, areOpenedFilters: false }));
	}

	openFieldsSelector = () => {
		this.setState((state) => ({ ...state, isOpenedFieldSelector: true }));
	}

	closeFieldsSelector = () => {
		this.setState((state) => ({ ...state, isOpenedFieldSelector: false }));
	}

	handleAddField = async (metadata) => {
		await this.props.onColumnAdd(metadata, { columnIndex: this.state.currentContextMenuColumnIndex ?? this.lastColumnIndex });

		const refreshDataOnChange = this.props.columnsManagement?.refreshDataOnChange ?? true;

		if (refreshDataOnChange) {
			this.loadData({
				pagination: {
					...this.loadMetadata.pagination,
					start: 0,
					size: this.props.loadItemsCount,
				},
			});
		}
	}

	get lastColumnIndex() {
		const columns = this.props.config.columns ?? [];

		const defaultIndex = columns.length - 1;
		const lastColumn = columns[columns.length];

		return lastColumn?.index ?? defaultIndex;
	}

	get columns() {
		const { config: { columns }, serverSorting } = this.props;

		return columns.map(({ Header, sortable, disableActions, index, ...otherProps }, i) => {
			return {
				...otherProps,
				Header: this.renderHeaderCell({ 
					disableActions,
					index: index === undefined ? i : index,
					content: Header, 
					sortable: serverSorting && sortable, 
				}),
				sortable: serverSorting ? false : sortable,
			};
		});
	}

	handleColumnActionClick = (callback) => () => {
		callback({ columnIndex: this.state.currentContextMenuColumnIndex });
	}

	handleColumnDelete = () => {
		return this.props.onColumnRemove({ columnIndex: this.state.currentContextMenuColumnIndex });
	}

	get withHeaderActions() {
		return Boolean(this.props.headerActions.length);
	}

	get withColumnActions() {
		return Boolean(this.props.columnActions.length);
	}

	get withFilters() {
		return this.props.columnActions.some(({ type }) => type === TABLE_COLUMN_PREDEFINED_ACTION_TYPE.filters);
	}

	get withColumnsAdd() {
		return this.props.columnActions.some(({ type }) => type === TABLE_COLUMN_PREDEFINED_ACTION_TYPE.addColumn);
	}

	get withColumnsDelete() {
		return this.props.columnActions.some(({ type }) => type === TABLE_COLUMN_PREDEFINED_ACTION_TYPE.deleteColumn);
	}

	get processedHeaderActions() {
		const { headerActions } = this.props;
		const predefinedActionsConfig = {
			[TABLE_COLUMN_PREDEFINED_ACTION_TYPE.addColumn]: { 
				config: { label: I18n.t('Add Column'), onClick: this.openFieldsSelector },
			},
		};
		
		const processedHeaderActions = [];
		
		headerActions.forEach(({ type, ...otherProps }) => {
			if (predefinedActionsConfig[type]) {
				const config = {
					...predefinedActionsConfig[type].config,
					...otherProps,
				};

				processedHeaderActions.push(config);

				return;
			}

			processedHeaderActions.push({
				...otherProps,
				onClick: this.handleColumnActionClick(otherProps.onClick)
			});
		});

		return processedHeaderActions;
	}

	get processedColumnActions() {
		const { columnActions } = this.props;
		const { currentContextMenuColumnIndex } = this.state;
		const columnSelected = currentContextMenuColumnIndex !== DEFAULT_COLUMN_INDEX_VALUE;
		const columnToBeFiltered = this.loadMetadata.filtering?.columnToBeFiltered || [];
		const columnFiltered = columnToBeFiltered.some((index) => index == currentContextMenuColumnIndex);

		const predefinedActionsConfig = {
			[TABLE_COLUMN_PREDEFINED_ACTION_TYPE.filters]: { 
				show: columnSelected,
				config: { label: I18n.t('Filter'), onClick: this.openFilters },
			},
			[TABLE_COLUMN_PREDEFINED_ACTION_TYPE.addColumn]: { 
				show: columnSelected,
				config: { label: I18n.t('Add Column'), onClick: this.openFieldsSelector },
			},
			[TABLE_COLUMN_PREDEFINED_ACTION_TYPE.deleteColumn]: { 
				show: columnSelected,
				config: { label: I18n.t('Delete Column'), onClick: this.handleColumnDelete },
			},
		};
		const clearFilterAction = { label: I18n.t('Clear Filter'), onClick: this.clearFilters };
		const shouldShowClearFilter = this.withFilters && columnFiltered && this.loadMetadata.filtering?.filter;
		const processedColumnActions = [];
		
		columnActions.forEach(({ type, ...otherProps }) => {
			if (predefinedActionsConfig[type]) {
				if (predefinedActionsConfig[type]?.show) {
					const config = {
						...predefinedActionsConfig[type].config,
						...otherProps,
					};
					processedColumnActions.push(config);
				}

				if (type === TABLE_COLUMN_PREDEFINED_ACTION_TYPE.filters && shouldShowClearFilter) {
					processedColumnActions.push(clearFilterAction);
				}

				return;
			}

			processedColumnActions.push({
				...otherProps,
				onClick: this.handleColumnActionClick(otherProps.onClick)
			});
		});

		return processedColumnActions;
	}

	handleRowClick = (e, selectedItem) => {
		e.persist();
		const { idProperty } = this.props;

		this.setState(
			(state) => { 
				let newSelectedItems = [selectedItem];

				if (e.ctrlKey || e.shiftKey) {
					const isAlreadySelected = state.selectedItems.some((x) => x[idProperty] === selectedItem[idProperty]);

					newSelectedItems = isAlreadySelected
						? state.selectedItems.filter((x) => x[idProperty] !== selectedItem[idProperty])
						: state.selectedItems.concat(selectedItem);
				}

				return {
					...state, 
					selectedItems: newSelectedItems,
				};
			},
			() => {
				const { onRowClick = () => {}, onSelectedItemsChange = () => {} } = this.props;
		
				onRowClick(selectedItem, this.state.selectedItems);
				onSelectedItemsChange(this.state.selectedItems)
			}
		);
	}

	scrollToTop = () => {
		if (!this.scrollElement) {
			return;
		}

		this.scrollElement.scrollTop = 0;
	}

	handleRowContextMenu = (e, item) => {
		e.persist();
		const isSelectedItem = this.state.selectedItems.some((selectedItem) => {
			return selectedItem[this.props.idProperty] === item[this.props.idProperty];
		});

		if (isSelectedItem && this.state.selectedItems.length > 1) {
			this.setState((state) => ({
				...state,
				rowContextMenuPosition: {
					x: e.clientX,
					y: e.clientY,
				}
			}));
		} else {
			this.setState((state) => ({
				...state,
				selectedItems: [],
			}));
			
			if (this.props.onSelectedItemsChange) {
				this.props.onSelectedItemsChange([]);
			}
		}
	}

	getTrProps = (state, rowInfo, column) => {
		const { config: { getTrProps = () => ({}) }, idProperty } = this.props;
		const { selectedItems = [] } = this.state;
		const trProps = getTrProps(state, rowInfo, column);
		const isSelected = selectedItems.some((x) => x[idProperty] === rowInfo.original[idProperty]);
		const style = trProps.style || {};
		const computedBackground = isSelected ? HIGHLIGHT_BACKGROUND_COLOR : 'transparent';

		let background = style.background || style.backgroundColor || computedBackground;

		return {
			...trProps,
			style: {
				...style,
				background,
			},
			onContextMenu: (e) => {
				const { rowContextMenuActions } = this.props;
				const { onContextMenu } = trProps;

				if (onContextMenu) {
					onContextMenu(e);
				}

				if (rowContextMenuActions?.length) {
					this.handleRowContextMenu(e, rowInfo.original);
				}
			},
			onClick: (e, handleOriginal) => {
				const { onRowClick, onSelectedItemsChange } = this.props;
				const { onClick } = trProps;

				if (onClick) {
					onClick(e);
				}

				if (onRowClick || onSelectedItemsChange) {
					this.handleRowClick(e, rowInfo.original);
				}

				if (handleOriginal) {
					handleOriginal();
				}
			},
		}
	}

	closeRowContextMenu = () => {
		this.setState((x) => ({
			...x,
			rowContextMenuPosition: undefined,
		}));
	}

	get currentContextMenuColumn() {
		return this.props.config.columns?.find((x, i) => this.state.currentContextMenuColumnIndex === i);
	}
	
	getMenuHorizontalPosition = (clickPosition) => {
		const { innerWidth } = window;
		const half = innerWidth / 2;

		if (clickPosition > half) {
			return {
				right: innerWidth - clickPosition,
			};
		}

		return {
			left: clickPosition,
		};
	}

	render() {
		const { areOpenedHeaderActions, areOpenedFilters, isOpenedFieldSelector, currentContextMenuColumnIndex, rowContextMenuPosition, currentHeaderActions } = this.state;
		const { config, noDataMessage = 'There is no data', className, filters, getFolders, getFields, rowContextMenuActions, moduleName } = this.props;
		const pageSize = config.pageSize || config.data.length;
		const noData = !config.data.length;
		const columns = config.columns || [];

		return (
			<React.Fragment>
				{isOpenedFieldSelector && (
					<FieldSelector 
						withSearch
						getFolders={getFolders}
						getFields={getFields}
						onChange={this.handleAddField}
						close={this.closeFieldsSelector}
					/>
				)}
				{areOpenedFilters && (
					<Filters
						moduleName={moduleName}
						filterName={this.currentContextMenuColumn?.Header}
						defaultValue={this.denormalizedFiltersValue[currentContextMenuColumnIndex]}
						filters={filters}
						onClose={this.closeFilters}
						onApply={this.applyFilters}
						{...columns[currentContextMenuColumnIndex]?.filterProps}
					/>
				)}
				{areOpenedHeaderActions && (
					<Actions 
						style={{
							...this.getMenuHorizontalPosition(this.headerActionsCoordinates.x),
							top: this.headerActionsCoordinates.y,
						}}
						className="Table__actions"
						items={currentHeaderActions}
						onClose={this.closeHeaderActions}
					/>
				)}
				{rowContextMenuPosition && (
					<Actions 
						style={{
							...this.getMenuHorizontalPosition(rowContextMenuPosition.x),
							top: rowContextMenuPosition.y,
						}}
						data={{
							selectedItems: this.state.selectedItems,
						}}
						className="Table__actions"
						items={rowContextMenuActions}
						onClose={this.closeRowContextMenu}
					/>
				)}
				<ReactTable
					{...{ 
						...config, 
						pageSize, 
						columns: this.columns,
						getTrProps: this.getTrProps,
						getTdProps: this.props.config?.getTdProps
					}}
					noDataText={noDataMessage}
					getProps={this.getRootProps}
					className={cx(
						'Table', 
						{ 
							'Table--empty': noData 
						}, 
						className
					)}
					showPagination={false}
					sortable={false}
					getResizerProps={getDefaultResizerProps}
				/>
			</React.Fragment>
		);
	}
}